import { useContext, useEffect, useMemo, useState } from 'react';

import LazyLoad from 'react-lazy-load';
import { getMemoizedData } from '../../../common/utils/memo';
import UserContext, { UserProfileContext } from '../../../common/utils/getContext';
import uikit from '../../../project/uikit/styles';
import { useRouter } from 'next/router';
import { getData } from '../../../common/utils/router';
import RenderElemets from './components/RenderElemets';
import { useWindowDimensions } from '../../../common/utils/windowDimensions';
import { getBreakpoint } from './components/patterns';
import PropTypes from 'prop-types';
import css from './Skeleton.module.scss';
import classNames from 'classnames';
import { generateStylers } from '../../../common/utils/styles';

const Skeleton = ({
    Component,
    componentProps,
    lazy = false,
    url,
    pattern,
    patternSettings,
    elements,
    wrapperStyles,
    handler,
    itemsShiners,
    wait,
    plug,
    plugCondition = false,
    darkMode,
    acceptCurrency
}) => {
    const breakpoint = getBreakpoint(useWindowDimensions());
    const elementsData = useMemo(
        () => (pattern ? pattern(breakpoint, patternSettings) : elements || []),
        [patternSettings, breakpoint, elements, pattern]
    );
    const [isVisible, setIsVisible] = useState(!lazy);
    const [data, setData] = useState(!url && !handler);
    const context = useContext(UserProfileContext);
    const loaded = context ? context.loaded || context.mainPage : true;
    const router = useRouter();

    const { userToken, lang, localization } = useContext(UserContext);
    useEffect(() => {
        (async () => {
            if (!router.isFallback && isVisible && loaded) {
                if (handler) {
                    setData(false);
                    const dataRes = await handler();
                    setData(dataRes || {});
                } else if (url) {
                    setData(false);
                    if (context?.adminPage) {
                        const dataRes = await getData(url, userToken, lang, false, localization?.currency);
                        setData(dataRes || {});
                    } else {
                        const dataRes = await getMemoizedData(url, userToken, lang, acceptCurrency || localization?.currency);
                        setData(dataRes || {});
                    }
                }
            }
        })();
    }, [
        router.isFallback,
        isVisible,
        lang,
        url,
        userToken,
        loaded,
        context?.adminPage,
        handler,
        localization?.currency,
        acceptCurrency
    ]);

    const { className, style } = generateStylers(wrapperStyles);
    const is_wait_stopped = wait || (wait === undefined && !router.isFallback && isVisible && data && loaded);
    if (is_wait_stopped && !plugCondition) {
        return <Component response={data} {...componentProps} />;
    }

    if (plugCondition && plug && is_wait_stopped) {
        return plug;
    }
    return (
        <div
            className={className}
            style={{
                ...uikit.positionRelative,
                overflow: 'hidden',
                ...style
            }}
        >
            {lazy && !data ? (
                <LazyLoad
                    offset={500}
                    onContentVisible={() => {
                        setIsVisible(true);
                    }}
                >
                    <></>
                </LazyLoad>
            ) : null}
            <RenderElemets darkMode={darkMode} itemsShiners={itemsShiners} elements={elementsData} />
            {itemsShiners ? null : <div className={classNames(css.shine, css.shiner, darkMode ? css.shiner_dark : '')} />}
        </div>
    );
};

Skeleton.propTypes = {
    Component: PropTypes.oneOfType([PropTypes.element, PropTypes.func]),
    componentProps: PropTypes.object,
    lazy: PropTypes.bool,
    url: PropTypes.string,
    pattern: PropTypes.func,
    patternSettings: PropTypes.object,
    elements: PropTypes.array,
    wrapperStyles: PropTypes.object,
    handler: PropTypes.func,
    itemsShiners: PropTypes.bool,
    wait: PropTypes.bool,
    darkMode: PropTypes.bool
};
export default Skeleton;
